import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus } from 'lucide-react';

import { Priority, Project, Sprint, Task } from '../types';
import { useApp } from '../providers/AppProvider';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek, startOfDay } from 'date-fns';
import { useOrganizationData } from '../hooks/useOrganizationData';
import { parseDateLike } from '../utils/dateUtils';
import SurfaceCard from '../components/SurfaceCard';
import { getEventTypeColors } from '../constants/colorScheme';

type CalendarProps = {
  projectScopeId?: string;
  projectScopeName?: string;
};

type CalendarEvent = {
  id: string;
  title: string;
  date: Date;
  type: 'milestone' | 'task' | 'meeting' | 'deadline' | 'update';
  priority?: Priority;
  status?: string;
  projectId?: string;
  projectName?: string;
};

export default function Calendar({ projectScopeId, projectScopeName }: CalendarProps = {}) {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [filterType, setFilterType] = useState<'all' | 'milestone' | 'task' | 'deadline' | 'update'>('all');

  const { state, currentDate, setCurrentDate, loading } = useApp();
  const { projects, tasks, sprints } = useOrganizationData(state, {
    includeAllOrganizations: true,
  });

  const normalizeIdentity = (value: unknown): string => {
    return String(value || '').trim().toLowerCase();
  };

  const currentUserIdentitySet = useMemo(() => {
    const identities = new Set<string>();

    const register = (value: unknown) => {
      const normalized = normalizeIdentity(value);
      if (!normalized) {
        return;
      }

      identities.add(normalized);

      const withoutCommonPrefix = normalized.replace(/^user[-_]/, '');
      if (withoutCommonPrefix) {
        identities.add(withoutCommonPrefix);
      }

      const compact = normalized.replace(/[^a-z0-9]/g, '');
      if (compact) {
        identities.add(compact);
      }

      if (normalized.includes('@')) {
        const localPart = normalized.split('@')[0];
        if (localPart) {
          identities.add(localPart);
          identities.add(localPart.replace(/[^a-z0-9]/g, ''));
        }
      }
    };

    register(state?.currentUser?.id);
    register(state?.currentUser?.email);

    return identities;
  }, [state?.currentUser?.id, state?.currentUser?.email]);

  const scopedProjects = useMemo<Project[]>(() => {
    if (!projectScopeId) return projects as Project[];
    return (projects as Project[]).filter((p) => String(p.id) === String(projectScopeId));
  }, [projects, projectScopeId]);

  const scopedTasks = useMemo<Task[]>(() => {
    if (!projectScopeId) return tasks as Task[];
    return (tasks as Task[]).filter((t) => String(t.projectId) === String(projectScopeId));
  }, [tasks, projectScopeId]);

  const scopedSprints = useMemo<Sprint[]>(() => {
    if (!projectScopeId) return sprints as Sprint[];
    return (sprints as Sprint[]).filter((s) => String(s.projectId) === String(projectScopeId));
  }, [sprints, projectScopeId]);

  const scopeLabel = projectScopeName || (scopedProjects[0] as Project | undefined)?.name;

  const events = useMemo<CalendarEvent[]>(() => {
    const nextEvents: CalendarEvent[] = [];

    const isAssignedToCurrentUser = (task: Task): boolean => {
      if (currentUserIdentitySet.size === 0) {
        return false;
      }

      return (task.assigneeIds || []).some((assigneeId) => {
        const normalized = normalizeIdentity(assigneeId);
        if (!normalized) {
          return false;
        }

        if (currentUserIdentitySet.has(normalized)) {
          return true;
        }

        const compact = normalized.replace(/[^a-z0-9]/g, '');
        if (compact && currentUserIdentitySet.has(compact)) {
          return true;
        }

        const withoutCommonPrefix = normalized.replace(/^user[-_]/, '');
        if (withoutCommonPrefix && currentUserIdentitySet.has(withoutCommonPrefix)) {
          return true;
        }

        if (normalized.includes('@')) {
          const localPart = normalized.split('@')[0];
          return (
            currentUserIdentitySet.has(localPart) ||
            currentUserIdentitySet.has(localPart.replace(/[^a-z0-9]/g, ''))
          );
        }

        return false;
      });
    };

    scopedTasks.forEach((task: Task) => {
      const taskDate = parseDateLike(
        task.targetEndDate ||
        task.targetCompletionDate ||
        (task as { targetDate?: string }).targetDate
      );
      if (!taskDate) return;

      const project = scopedProjects.find((p: Project) => p.id === task.projectId);

      nextEvents.push({
        id: task.id,
        title: task.title,
        date: taskDate,
        type: 'task',
        priority: task.priority as Priority,
        status: task.status,
        projectId: task.projectId,
        projectName: project?.name,
      });

      if (['P0', 'P1', 'P2'].includes(task.priority)) {
        nextEvents.push({
          id: `milestone-${task.id}`,
          title: `🎯 ${task.title}`,
          date: taskDate,
          type: 'milestone',
          priority: task.priority as Priority,
          status: task.status,
          projectId: task.projectId,
          projectName: project?.name,
        });
      }

      const nextUpdateDate = parseDateLike(task.nextUpdateDate);
      if (nextUpdateDate && isAssignedToCurrentUser(task)) {
        nextEvents.push({
          id: `update-${task.id}`,
          title: `Update Due: ${task.title}`,
          date: nextUpdateDate,
          type: 'update',
          status: task.status,
          projectId: task.projectId,
          projectName: project?.name,
        });
      }
    });

    scopedSprints.forEach((sprint: Sprint) => {
      const sprintEndDate = parseDateLike(sprint.endDate);
      if (!sprintEndDate) return;

      const project = scopedProjects.find((p: Project) => p.id === sprint.projectId);
      nextEvents.push({
        id: sprint.id,
        title: `Sprint: ${sprint.name}`,
        date: sprintEndDate,
        type: 'deadline',
        projectId: sprint.projectId,
        projectName: project?.name,
      });
    });

    return nextEvents;
  }, [scopedProjects, scopedSprints, scopedTasks, currentUserIdentitySet]);

  // Calendar calculations
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const getEventsForDay = (day: Date): CalendarEvent[] => {
    return events.filter(event => {
      if (filterType !== 'all' && event.type !== filterType) return false;
      return isSameDay(event.date, day);
    });
  };

  const goToPreviousMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const goToNextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const goToToday = () => setCurrentDate(new Date());

  const getEventColor = (event: CalendarEvent): string => {
      let eventKey: string = event.type;
      if (event.type === 'milestone' && event.priority) {
        eventKey = `milestone_${event.priority.toLowerCase()}`;
      } else if (event.type === 'task' && (event.status === 'Completed' || event.status === 'Done')) {
        eventKey = 'completed';
      } else if (event.type === 'update') {
        eventKey = 'update_due';
      } else if (event.type === 'deadline') {
        eventKey = 'sprint_deadline';
      }
      const colors = getEventTypeColors(eventKey);
      return `${colors.background} ${colors.text}`;
  };

  const getTaskEventId = (eventId: string): string => {
    return eventId.replace('milestone-', '').replace('update-', '');
  };

  const selectedDayEvents = selectedDate ? getEventsForDay(selectedDate) : [];

  if (loading || !state) {
    return <div className="p-8 text-center text-slate-500 dark:text-slate-400">Loading calendar...</div>;
  }

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="mb-2 text-3xl text-slate-800 dark:text-slate-100">Calendar</h1>
            <p className="text-slate-500 dark:text-slate-400">
              {projectScopeId
                ? `Milestones, deadlines, and events for ${scopeLabel || 'this project'}`
                : 'Milestones, Deadlines, and Events'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              title="Filter calendar events"
              aria-label="Filter calendar events"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as any)}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-blue-700"
            >
              <option value="all">All Events</option>
              <option value="milestone">Milestones</option>
              <option value="task">Tasks</option>
              <option value="update">Update Due</option>
              <option value="deadline">Deadlines</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar View */}
        <div className="lg:col-span-2">
          <SurfaceCard className="p-6">
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">
                {format(currentDate, 'MMMM yyyy')}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={goToToday}
                  className="rounded border border-blue-200 bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700 transition-colors hover:border-blue-300 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300 dark:hover:bg-blue-900/50"
                >
                  Today
                </button>
                <button
                  onClick={goToPreviousMonth}
                  title="Go to previous month"
                  aria-label="Go to previous month"
                  className="rounded p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={goToNextMonth}
                  title="Go to next month"
                  aria-label="Go to next month"
                  className="rounded p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Weekday Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="py-2 text-center text-sm font-medium text-slate-500 dark:text-slate-400">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, index) => {
                const dayEvents = getEventsForDay(day);
                const isToday = isSameDay(day, new Date());
                const isCurrentMonth =
                  day.getMonth() === currentDate.getMonth() &&
                  day.getFullYear() === currentDate.getFullYear();
                const isSelected = selectedDate && isSameDay(day, selectedDate);

                return (
                  <div
                    key={index}
                    onClick={() => setSelectedDate(day)}
                    className={`min-h-24 cursor-pointer rounded border p-2 transition-colors ${
                      !isCurrentMonth
                        ? 'border-slate-200 bg-slate-50 text-slate-400 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-500'
                        : 'border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800'
                    } ${isSelected ? 'ring-2 ring-blue-300 dark:ring-blue-700' : ''}`}
                  >
                    <div
                      className={`text-sm font-medium mb-1 ${
                        isToday
                          ? 'flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-white dark:bg-blue-500'
                          : ''
                      }`}
                    >
                      {format(day, 'd')}
                    </div>
                    <div className="space-y-1">
                      {dayEvents.slice(0, 3).map(event => (
                        <div
                          key={event.id}
                          className={`text-xs p-1 rounded truncate ${getEventColor(event)}`}
                          title={event.title}
                        >
                          {event.title}
                        </div>
                      ))}
                      {dayEvents.length > 3 && (
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          +{dayEvents.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </SurfaceCard>
        </div>

        {/* Event Details Sidebar */}
        <div className="lg:col-span-1">
          <SurfaceCard className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <CalendarIcon className="w-5 h-5" />
              {selectedDate ? format(selectedDate, 'MMMM d, yyyy') : 'Select a Date'}
            </h3>

            {selectedDate && selectedDayEvents.length > 0 ? (
              <div className="space-y-3">
                {selectedDayEvents.map(event => (
                  <div
                    key={event.id}
                    className="rounded-lg border border-border p-3 transition-shadow hover:shadow-sm"
                  >
                    <div className="flex items-start gap-2 mb-2">
                      <div className={`w-2 h-2 rounded-full mt-1 ${getEventColor(event)}`} />
                      <div className="flex-1">
                        {event.type === 'task' || event.type === 'milestone' || event.type === 'update' ? (
                          <Link
                            to={`/task/${getTaskEventId(event.id)}`}
                            className="font-medium transition-colors hover:text-blue-700 dark:hover:text-blue-300"
                          >
                            {event.title}
                          </Link>
                        ) : (
                          <p className="font-medium">{event.title}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {event.type === 'milestone' && '🎯 Milestone'}
                          {event.type === 'task' && '📋 Task'}
                          {event.type === 'update' && '🔔 Update Due'}
                          {event.type === 'deadline' && '⏰ Deadline'}
                        </p>
                        {event.projectName && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            Project: {event.projectName}
                          </p>
                        )}
                        {event.status && (
                          <p className="text-xs text-muted-foreground">
                            Status: {event.status}
                          </p>
                        )}
                        {event.priority && (
                          <p className="text-xs font-medium text-muted-foreground">
                            Priority: {event.priority}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : selectedDate ? (
              <p className="text-sm text-muted-foreground">No events on this date</p>
            ) : (
              <p className="text-sm text-muted-foreground">Select a date to view events</p>
            )}
          </SurfaceCard>

          {/* Upcoming Events */}
          <SurfaceCard className="mt-6 p-6">
            <h3 className="text-lg font-semibold mb-4">Upcoming Events</h3>
            <div className="space-y-3">
              {events
                .filter(e => e.date >= startOfDay(new Date()))
                .sort((a, b) => a.date.getTime() - b.date.getTime())
                .slice(0, 5)
                .map(event => (
                  <div key={event.id} className="flex items-start gap-3">
                    <div className={`w-2 h-2 rounded-full mt-1.5 ${getEventColor(event)}`} />
                    <div className="flex-1">
                      {event.type === 'task' || event.type === 'milestone' || event.type === 'update' ? (
                        <Link
                          to={`/task/${getTaskEventId(event.id)}`}
                          className="text-sm font-medium transition-colors hover:text-blue-700 dark:hover:text-blue-300"
                        >
                          {event.title}
                        </Link>
                      ) : (
                        <p className="text-sm font-medium">{event.title}</p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {format(event.date, 'MMM d, yyyy')}
                      </p>
                    </div>
                  </div>
                ))}
              {events.filter(e => e.date >= startOfDay(new Date())).length === 0 && (
                <p className="text-sm text-muted-foreground">No upcoming events</p>
              )}
            </div>
          </SurfaceCard>

          {/* Legend */}
          <SurfaceCard className="mt-6 p-6">
            <h3 className="text-sm font-semibold mb-3">Legend</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-destructive/10" />
                <span className="text-xs">P0 Milestone</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-chart-5/10" />
                <span className="text-xs">P1 Milestone</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-chart-5/10" />
                <span className="text-xs">P2 Milestone</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-primary/10" />
                <span className="text-xs">Task</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-chart-5/10" />
                <span className="text-xs">Update Due</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-chart-4/10" />
                <span className="text-xs">Sprint Deadline</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-chart-2/10" />
                <span className="text-xs">Completed</span>
              </div>
            </div>
          </SurfaceCard>
        </div>
      </div>
    </div>
  );
}
